import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { Icon, tokens, Tooltip } from '@hotjar/hotsauce';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';

import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useStoreState } from '@hotjar/recordings/src/store/player/context';
import type { EventMarker } from '../../types';
import {
  DEFAULT_MARKER_Z_INDEX,
  DURATION_MARKER_Z_INDEX,
  EXPANDED_MARKER_Z_INDEX,
  ICON_MARKER_Z_INDEX,
} from './constants';
import { getIconName } from '../../EventMarkerUtils';
import { useRealTimeStore } from '../../store/realTimeStore';
import { DEFAULT_UNDERLINE_HEIGHT, FILTER_MATCH_TOOLTIP_COLOR } from '../../Underline/constants';
import { UnderlineDiv } from '../../Underline/Underline';
import { autoHideTooltipAfterTimeout } from '../../utils/autoHideTooltip';

const DEFAULT_BACKGROUND_COLOR = tokens.color.white;

const getIconColor = (eventMarker: EventMarker): string => {
  switch (eventMarker.type) {
    case 'tag_recording': {
      return tokens.color.neutral_700;
    }
    case 'viewport_resize':
      return tokens.color.white;
    case 'autotag_recording': {
      const behaviorName = eventMarker.subtype ?? eventMarker.name;
      switch (behaviorName) {
        case 'poll':
          return tokens.color.lilac;
        default:
          return 'inherited';
      }
    }
    case 'heatmap_screenshot':
      return tokens.color.neutral_800;
    case 'error':
      return tokens.color.text_danger;
    default:
      return 'inherited';
  }
};

const getBackgroundColor = (eventMarker: EventMarker): string => {
  switch (eventMarker.type) {
    case 'key_press':
    case 'select_change':
    case 'input_choice_change':
      return '#61FF7E';
    case 'tag_recording':
      return '#5dffd3';
    case 'viewport_resize':
      return tokens.BRAND_COLORS.neutral_700;
    case 'mouse_click':
      return tokens.color.neutral_100;
    case 'autotag_recording': {
      const behaviorName = eventMarker.subtype ?? eventMarker.name;
      switch (behaviorName) {
        case 'rageclick':
          return '#FFD166';
        case 'uturn':
          return '#FFFF8D';
        case 'wildscroll':
        case 'poll':
        case 'feedback':
          return DEFAULT_BACKGROUND_COLOR;
        default:
          return '#5DFFD3';
      }
    }
    case 'error':
      return tokens.color.white;
    case 'heatmap_screenshot':
      return tokens.color.white;
    default:
      return DEFAULT_BACKGROUND_COLOR;
  }
};

const incomingFeedbackTrackingMap: Record<string, string> = {
  'feedback.sentiment.e:4': 'incoming_love',
  'feedback.sentiment.e:3': 'incoming_like',
  'feedback.sentiment.e:2': 'incoming_neutral',
  'feedback.sentiment.e:1': 'incoming_dislike',
  'feedback.sentiment.e:0': 'incoming_hate',
};

const getMappedEventMarkerName = (eventMarker: EventMarker): string => {
  if (eventMarker.name?.startsWith('feedback')) {
    const result = eventMarker.name.match(/^(feedback\.sentiment\.e:[0-4]{1})/gm);
    if (result && result.length > 0) {
      return incomingFeedbackTrackingMap[result[0]];
    }
  }
  return eventMarker.name ?? eventMarker.type;
};

const SIMPLE_ANCHOR_SIZE = 4;
const ICON_ANCHOR_SIZE = 16;

const AnchorBase = styled.div<{
  backgroundColor?: string;
}>`
  position: absolute;
  width: ${SIMPLE_ANCHOR_SIZE}px;
  height: ${SIMPLE_ANCHOR_SIZE}px;
  border-radius: ${SIMPLE_ANCHOR_SIZE}px;
  cursor: pointer;
  background: ${({ backgroundColor }) => backgroundColor || DEFAULT_BACKGROUND_COLOR};
`;

const DefaultAnchorDiv = styled(AnchorBase)<{
  backgroundColor?: string;
}>`
  transition: all 0.1s ease-out;
`;

const IconAnchorDiv = styled(AnchorBase)<{
  backgroundColor?: string;
  color?: string;
}>`
  width: ${ICON_ANCHOR_SIZE}px;
  height: ${ICON_ANCHOR_SIZE}px;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px;
  color: ${({ color }) => color ?? 'inherited'};

  transition: all 0.1s ease-out;
`;

const ScreenshotAnchorDiv = styled(IconAnchorDiv)`
  width: ${tokens.spacer.x4};
  height: ${tokens.spacer.x4};
  padding: ${tokens.spacer.x1};
  background-color: ${tokens.color.ui_background};
  border-radius: ${tokens.spacer.x0};
  bottom: ${tokens.spacer.x2};
  ${tokens.elevation(4)}
`;

const StyledEmotionIcon = styled(EmotionIcon)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DurationAnchorDiv = styled(AnchorBase)<{
  backgroundColor?: string;
}>`
  transition: all 0.1s ease-out;
`;

const EventUnderlineDiv = styled(UnderlineDiv)`
  top: 12px;
  bottom: 0;
  width: ${ICON_ANCHOR_SIZE}px;
  height: ${DEFAULT_UNDERLINE_HEIGHT};
`;

interface Props {
  eventMarker: EventMarker;
  xPositionInPx: number;
  onMouseDown: (clientX: number, uiPosition: string) => void;
  isDesktopRecording: boolean;
  setIsHoveringMarker: (state: boolean) => void;
  showFilterMatchTooltip: boolean;
  firstFilterMatch: EventMarker | null;
}

export const EventMarkerElement = forwardRef<HTMLDivElement | undefined, Props>(
  (
    {
      eventMarker,
      xPositionInPx,
      isDesktopRecording,
      onMouseDown,
      setIsHoveringMarker,
      showFilterMatchTooltip,
      firstFilterMatch,
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const elementDivRef = useRef<HTMLDivElement>(null);
    const { openConsole } = useStoreState();
    const { t } = useTranslation();

    useImperativeHandle(ref, () => elementDivRef.current ?? undefined);

    // Useful to disable the event marker tooltips when we are dragging
    useEffect(() => {
      return useRealTimeStore.subscribe(
        (isDragging: boolean) => {
          if (!elementDivRef.current) {
            return;
          }
          if (isDragging) {
            elementDivRef.current.style.pointerEvents = 'none';
          } else {
            elementDivRef.current.style.pointerEvents = 'inherit';
          }
        },
        (state) => state.isDragging,
      );
    }, [elementDivRef]);

    const setIsHoveringMarkerHandle = useCallback(
      (newIsHovered: boolean) => {
        setIsHovered(newIsHovered);
        setIsHoveringMarker(newIsHovered);
      },
      [setIsHoveringMarker],
    );

    const onMouseDownWrapper = useCallback(
      (e) => {
        if (eventMarker.type === 'error') {
          openConsole(eventMarker.time);
        }

        onMouseDown(
          e.clientX,
          `clicked_on_timeline_marker-${getMappedEventMarkerName(eventMarker)}`,
        );
      },
      [openConsole, onMouseDown, eventMarker],
    );
    const leftPositionIconAnchor = xPositionInPx - ICON_ANCHOR_SIZE / 2;
    const leftPositionSimpleAnchor = xPositionInPx - SIMPLE_ANCHOR_SIZE / 2;
    if (eventMarker.is_highlighted && !showFilterMatchTooltip) {
      return (
        <>
          <IconAnchorDiv
            backgroundColor={getBackgroundColor(eventMarker)}
            color={getIconColor(eventMarker)}
            ref={elementDivRef}
            onMouseDown={onMouseDownWrapper}
            onMouseEnter={() => setIsHoveringMarkerHandle(true)}
            onMouseLeave={() => setIsHoveringMarkerHandle(false)}
            style={{
              left: leftPositionIconAnchor,
              transform: `scale(${isHovered ? 1.3 : 1})`,
              zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
            }}
          >
            <Icon icon={getIconName(eventMarker, isDesktopRecording)} />
          </IconAnchorDiv>
          <EventUnderlineDiv
            style={{
              left: leftPositionIconAnchor,
              transform: `scale(${isHovered ? 1.3 : 1})`,
              marginTop: `${isHovered ? 5 : 2}px`,
              zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
            }}
          />
        </>
      );
    }

    if (eventMarker.is_highlighted && showFilterMatchTooltip) {
      return (
        <>
          <IconAnchorDiv
            backgroundColor={getBackgroundColor(eventMarker)}
            color={getIconColor(eventMarker)}
            ref={elementDivRef}
            onMouseDown={onMouseDownWrapper}
            onMouseEnter={() => setIsHoveringMarkerHandle(true)}
            onMouseLeave={() => setIsHoveringMarkerHandle(false)}
            style={{
              left: leftPositionIconAnchor,
              transform: `scale(${isHovered ? 1.3 : 1})`,
              zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
            }}
          >
            <Icon icon={getIconName(eventMarker, isDesktopRecording)} />
          </IconAnchorDiv>

          {/* need to add guards otherwise tooltip gets rendered on the very left of the container */}
          {leftPositionIconAnchor && (
            <Tooltip
              showOnCreate={eventMarker === firstFilterMatch}
              onShow={autoHideTooltipAfterTimeout}
              text={t('common:player.timeline.filter-match')}
              placement="bottom"
              css={css`
                background: ${FILTER_MATCH_TOOLTIP_COLOR};
                color: ${tokens.ALIAS_TEXT_COLORS.text_default};
                .tippy-arrow {
                  color: ${FILTER_MATCH_TOOLTIP_COLOR};
                }
              `}
            >
              <EventUnderlineDiv
                style={{
                  left: leftPositionIconAnchor,
                  transform: `scale(${isHovered ? 1.3 : 1})`,
                  marginTop: `${isHovered ? 5 : 2}px`,
                  zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
                }}
              />
            </Tooltip>
          )}
        </>
      );
    }

    if (eventMarker.type === 'autotag_recording') {
      const behaviorName = eventMarker.subtype ?? eventMarker.name;
      if (
        (behaviorName === 'poll' || behaviorName === 'feedback') &&
        eventMarker.autotag_properties?.e != null
      ) {
        return (
          <IconAnchorDiv
            backgroundColor={getBackgroundColor(eventMarker)}
            ref={elementDivRef}
            onMouseDown={onMouseDownWrapper}
            onMouseEnter={() => setIsHoveringMarkerHandle(true)}
            onMouseLeave={() => setIsHoveringMarkerHandle(false)}
            style={{
              left: leftPositionIconAnchor,
              transform: `scale(${isHovered ? 1.3 : 1})`,
              zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
            }}
          >
            <StyledEmotionIcon
              emotionStyle={behaviorName === 'feedback' ? 'default' : 'smiley'}
              score={eventMarker.autotag_properties.e as EmotionScore}
              size="xs"
            />
          </IconAnchorDiv>
        );
      }
      return (
        <IconAnchorDiv
          backgroundColor={getBackgroundColor(eventMarker)}
          ref={elementDivRef}
          onMouseDown={onMouseDownWrapper}
          onMouseEnter={() => setIsHoveringMarkerHandle(true)}
          onMouseLeave={() => setIsHoveringMarkerHandle(false)}
          color={getIconColor(eventMarker)}
          style={{
            left: leftPositionIconAnchor,
            transform: `scale(${isHovered ? 1.3 : 1})`,
            zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
          }}
        >
          <Icon icon={getIconName(eventMarker, isDesktopRecording)} />
        </IconAnchorDiv>
      );
    }

    if (eventMarker.type === 'tag_recording' || eventMarker.type === 'error') {
      return (
        <IconAnchorDiv
          backgroundColor={getBackgroundColor(eventMarker)}
          ref={elementDivRef}
          onMouseDown={onMouseDownWrapper}
          onMouseEnter={() => setIsHoveringMarkerHandle(true)}
          onMouseLeave={() => setIsHoveringMarkerHandle(false)}
          color={getIconColor(eventMarker)}
          style={{
            left: leftPositionIconAnchor,
            transform: `scale(${isHovered ? 1.3 : 1})`,
            zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
          }}
        >
          <Icon icon={getIconName(eventMarker, isDesktopRecording)} />
        </IconAnchorDiv>
      );
    }

    if (eventMarker.progress_duration) {
      return (
        <DurationAnchorDiv
          backgroundColor={getBackgroundColor(eventMarker)}
          ref={elementDivRef}
          onMouseDown={onMouseDownWrapper}
          onMouseEnter={() => setIsHoveringMarkerHandle(true)}
          onMouseLeave={() => setIsHoveringMarkerHandle(false)}
          style={{
            left: leftPositionSimpleAnchor,
            width: `${+eventMarker.progress_duration}%`,
            transform: `scaleY(${isHovered ? 2 : 1})`,
            zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : DURATION_MARKER_Z_INDEX,
          }}
        />
      );
    }

    // Custom event marker created for Screenshot Retaker in Heatmaps
    if (eventMarker.type === 'heatmap_screenshot') {
      return (
        <ScreenshotAnchorDiv
          backgroundColor={getBackgroundColor(eventMarker)}
          ref={elementDivRef}
          onMouseEnter={() => setIsHoveringMarkerHandle(true)}
          onMouseLeave={() => setIsHoveringMarkerHandle(false)}
          color={getIconColor(eventMarker)}
          style={{
            // Needs custom positioning on left as icon has bigger size & padding
            left: xPositionInPx - 14,
            transform: `scale(${isHovered ? 1.1 : 1})`,
            zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : ICON_MARKER_Z_INDEX,
          }}
        >
          <Icon icon={getIconName(eventMarker, isDesktopRecording)} />
        </ScreenshotAnchorDiv>
      );
    }

    return (
      <DefaultAnchorDiv
        backgroundColor={getBackgroundColor(eventMarker)}
        ref={elementDivRef}
        onMouseDown={onMouseDownWrapper}
        onMouseEnter={() => setIsHoveringMarkerHandle(true)}
        onMouseLeave={() => setIsHoveringMarkerHandle(false)}
        style={{
          left: leftPositionSimpleAnchor,
          transform: `scale(${isHovered ? 2 : 1})`,
          zIndex: isHovered ? EXPANDED_MARKER_Z_INDEX : DEFAULT_MARKER_Z_INDEX,
        }}
        data-testid="default-event-marker"
      />
    );
  },
);
